"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";

export type CarouselCover = { id: string; title: string; imageUrl: string };

function relativeOffset(index: number, active: number, total: number) {
  let offset = (index - active + total) % total;
  if (offset > total / 2) offset -= total;
  return offset;
}

export function CoverCarousel({ covers }: { covers: CarouselCover[] }) {
  const reducedMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const total = covers.length;
  const safeActive = total > 0 ? active % total : 0;

  useEffect(() => {
    if (reducedMotion || paused || total < 2) return;
    const timer = window.setInterval(() => setActive((value) => (value + 1) % total), 5600);
    return () => window.clearInterval(timer);
  }, [paused, reducedMotion, total]);

  if (total === 0) return null;

  const previous = () => setActive((value) => ((value % total) - 1 + total) % total);
  const next = () => setActive((value) => (value + 1) % total);

  return (
    <div
      className="relative mt-10 sm:mt-12"
      role="region"
      aria-label="首页封面轮播"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") previous();
        if (event.key === "ArrowRight") next();
      }}
    >
      <div
        className="relative h-[clamp(310px,46vw,570px)] touch-pan-y overflow-hidden [container-type:inline-size]"
        aria-live="polite"
        onTouchStart={(event) => {
          suppressClick.current = false;
          const touch = event.touches[0];
          touchStart.current = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
          setPaused(true);
        }}
        onTouchEnd={(event) => {
          const start = touchStart.current;
          touchStart.current = null;
          setPaused(false);
          if (!start || event.touches.length > 0 || total < 2) return;
          const touch = event.changedTouches[0];
          const deltaX = touch.clientX - start.x;
          const deltaY = touch.clientY - start.y;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          suppressClick.current = true;
          if (deltaX < 0) next();
          else previous();
        }}
        onTouchCancel={() => {
          touchStart.current = null;
          setPaused(false);
        }}
        onClickCapture={(event) => {
          if (!suppressClick.current) return;
          suppressClick.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {covers.map((cover, index) => {
          const offset = relativeOffset(index, safeActive, total);
          const distance = Math.abs(offset);
          const visible = distance <= 2;
          const selected = offset === 0;
          const scale = selected ? 1 : distance === 1 ? 0.78 : 0.64;
          const rotate = selected ? 0 : offset < 0 ? 3 : -3;
          // Keep the scaled side card inside the stage so overflow cannot cut off its corners.
          // cqw measures the stage; transform percentages measure this card's own width.
          const shift = selected ? "0px" : `${Math.sign(offset)} * min(${distance * 78}%, max(0px, 50cqw - ${scale * 50}% - 12px))`;
          const cardStyle = {
            opacity: visible ? (selected ? 1 : distance === 1 ? 0.62 : 0.28) : 0,
            transform: `translateX(calc(-50% + ${shift})) translateY(-50%) scale(${scale}) rotateY(${rotate}deg)`,
            zIndex: selected ? 30 : 20 - distance,
            pointerEvents: visible ? "auto" : "none",
          } as const;

          const image = (
            // Preserve the full artwork, including its embedded text.
            // eslint-disable-next-line @next/next/no-img-element
            <img src={cover.imageUrl} alt={selected ? cover.title : ""} className="block h-auto w-auto max-h-full max-w-full rounded-[clamp(20px,2.5vw,34px)]" />
          );

          return (
            <article
              key={cover.id}
              className="absolute left-1/2 top-1/2 flex items-center justify-center aspect-video w-[min(78vw,760px)] overflow-hidden rounded-[clamp(20px,2.5vw,34px)] border border-white/30 bg-[#071323] shadow-[0_28px_80px_-28px_rgba(10,34,48,0.6)] transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(.16,1,.3,1)] sm:w-[min(64vw,760px)] lg:w-[min(54vw,760px)]"
              style={cardStyle}
              aria-hidden={!visible}
            >
              {selected ? image : (
                <button
                  type="button"
                  className="flex size-full cursor-pointer items-center justify-center"
                  onClick={() => setActive(index)}
                  aria-label={`查看${cover.title}`}
                  tabIndex={visible ? 0 : -1}
                >
                  {image}
                  <span className="absolute inset-0 bg-[color-mix(in_oklab,var(--color-ink)_20%,transparent)]" aria-hidden="true" />
                </button>
              )}
            </article>
          );
        })}
      </div>

      <div className="mt-1 flex items-center justify-center">
        <div className="flex items-center gap-2" aria-label="封面轮播位置">
          {covers.map((cover, index) => (
            <button
              key={cover.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`查看第 ${index + 1} 张封面：${cover.title}`}
              aria-current={index === safeActive ? "true" : undefined}
              className={`h-1.5 rounded-full transition-all ${index === safeActive ? "w-8 bg-[var(--color-paper)]" : "w-1.5 bg-[var(--color-muted)]/55 hover:bg-[var(--color-mist)]"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
